<script setup>
import { computed } from 'vue';

import { HrChip } from '../primitives';

/**
 * Filter chip — a toggleable chip for activating or deactivating a filter criterion.
 *
 * @status recommended
 * @group chips
 * @useWhen Letting users toggle a single filter on or off
 * @avoidWhen Selecting from multiple options — use HrMultiSelect or HrListSelect
 * @related HrStatusActionChip
 */
defineOptions({ name: 'HrFilterChip' });

const props = defineProps({
  /**
   * Chip label text. Falls back to the default slot when null.
   * @replacesSlot default
   * @example Active
   */
  label: {
    type: String,
    default: null,
  },
  /** Selected/active state via v-model. A check icon appears when selected. */
  modelValue: {
    type: Boolean,
    default: false,
  },
  /**
   * Material icon shown ahead of the label while the chip is unselected. M3 replaces it with the
   * check on selection, so it identifies the option rather than repeating the selected state.
   * @example pending_actions
   */
  icon: {
    type: String,
    default: null,
  },
  /**
   * Chip size.
   * @values sm, md, lg
   */
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  /** Prevents interaction and dims the chip. */
  disable: {
    type: Boolean,
    default: false,
  },
  /** Renders a skeleton placeholder instead of the chip. */
  pending: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits({
  /** Fired when the chip is toggled; payload is the new boolean value. */
  'update:modelValue': null,
});

const effectiveIcon = computed(() => (props.modelValue ? 'check' : props.icon));

// @todo Remove legacy-props when Legacy theme is removed
const legacyProps = computed(() => (
  props.modelValue
    ? { color: 'primary', textColor: 'white' }
    : { outline: true }
));
</script>

<template>
  <hr-chip
    variant="filter"
    clickable
    :label="label"
    :icon="effectiveIcon"
    :size="size"
    :selected="modelValue"
    :disable="disable"
    :pending="pending"
    :legacy-props="legacyProps"
    v-bind="$attrs"
    @click="emit('update:modelValue', !modelValue)"
  >
    <!-- @slot default Custom chip label content; use instead of the `label` prop for rich content. -->
    <slot />
  </hr-chip>
</template>
